import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminSearchAnalyticsPage.css';

function AdminSearchAnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [topSearches, setTopSearches] = useState([]);
  const [zeroResults, setZeroResults] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [overviewRes, topRes, zeroRes] = await Promise.all([
        axios.get('/search-analytics/overview', { headers }),
        axios.get('/search-analytics/top-searches', { headers }),
        axios.get('/search-analytics/zero-results', { headers })
      ]);

      setOverview(overviewRes.data);
      setTopSearches(topRes.data);
      setZeroResults(zeroRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load search analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading analytics...</div></div>;
  }

  if (error) {
    return <div className="admin-container"><div className="error">{error}</div></div>;
  }

  return (
    <div className="admin-container">
      <h1>🔍 Search Analytics</h1>

      <div className="metrics-grid">
        <div className="metric-card">
          <h3>Total Searches</h3>
          <div className="metric-value">{overview?.total_searches?.toLocaleString() || 0}</div>
          <div className="metric-label">{overview?.unique_users || 0} unique users</div>
        </div>

        <div className="metric-card">
          <h3>Success Rate</h3>
          <div className="metric-value">
            {overview?.success_rate ? parseFloat(overview.success_rate).toFixed(1) : 0}%
          </div>
          <div className="metric-label">Searches with results</div>
        </div>

        <div className="metric-card">
          <h3>Click-Through Rate</h3>
          <div className="metric-value">
            {overview?.click_through_rate ? parseFloat(overview.click_through_rate).toFixed(1) : 0}%
          </div>
          <div className="metric-label">Searches leading to clicks</div>
        </div>

        <div className="metric-card">
          <h3>Avg Response Time</h3>
          <div className="metric-value">
            {overview?.avg_response_time_ms ? Math.round(overview.avg_response_time_ms) : 0}ms
          </div>
          <div className="metric-label">Search performance</div>
        </div>
      </div>

      <div className="analytics-grid">
        <div className="analytics-section">
          <h2>Top Searches (Last 30 Days)</h2>
          <div className="search-list">
            {topSearches.length > 0 ? (
              topSearches.map((search, index) => (
                <div key={index} className="search-item">
                  <div className="search-info">
                    <div className="search-query">{search.query_text}</div>
                    <div className="search-meta">
                      {search.unique_users} users • {parseFloat(search.success_rate || 0).toFixed(1)}% success
                    </div>
                  </div>
                  <div className="search-count">{search.search_count}</div>
                </div>
              ))
            ) : (
              <div className="empty-state">No search data yet</div>
            )}
          </div>
        </div>

        <div className="analytics-section">
          <h2 className="error-header">Zero Result Queries</h2>
          <div className="search-list">
            {zeroResults.length > 0 ? (
              zeroResults.map((search, index) => (
                <div key={index} className="search-item error-item">
                  <div className="search-info">
                    <div className="search-query">{search.query_text}</div>
                    <div className="search-meta">
                      {search.search_type} • Last seen: {new Date(search.last_seen_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="search-count error-count">{search.occurrence_count}×</div>
                </div>
              ))
            ) : (
              <div className="empty-state success">✅ No zero-result queries!</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminSearchAnalyticsPage;
